import { getCSRFToken, dispatchToast, toastFetchError } from "../shared/htmx-helpers";
import type { CmsEditor, CMSEditorConfig } from "./editor";
import { signalPublishStateChanged } from "../shared/publish-state-signal";
import {
  checkContentPlaceholders,
  insertPlaceholderToken,
  parseAllowed,
  renderPlaceholderError,
} from "./content-placeholders";

// placeholderRuleKey keys _placeholderRules by (target, field).
const placeholderRuleKey = (target: string, field: string) => `${target}\n${field}`;

// editorAutosave owns the per-field debounced draft-save pipeline: the pending
// edit/caption queues, the target→(content-path, overlay-shape) routing table
// (_targetRoute — the load-bearing shape contract), and the flush machinery.
// Every method reaches sibling concerns (preview refresh, revert-button sync,
// dirty flag) through `this`, so it composes into cmsEditor's merged object
// where those siblings live. `config` is captured here for the content-path /
// baseURL it needs.
export function editorAutosave(config: CMSEditorConfig): Partial<CmsEditor> & ThisType<CmsEditor> {
  return {
    _saveTimer: null as ReturnType<typeof setTimeout> | null,
    _capTimer: null as ReturnType<typeof setTimeout> | null,
    // _pending is keyed by autosave TARGET (e.g. "property:sections.host",
    // "site:homepage", "site:contact.details"); each value is the per-field
    // edits for that target. _flush groups by target → one PUT per
    // (content-path, overlay-shape). See _flush for the pinned shapes.
    _pending: {} as Record<string, Record<string, unknown>>,
    _pendingCaptions: {} as Record<string, Record<string, unknown>>,
    // Single-flight guard for _flush: overlapping flushPending()/publish() calls
    // must not both drain _pending — a second concurrent drain reads a
    // half-emptied queue and reports ok while the in-flight run is still
    // re-queuing a failed edit. _flushing marks a drain in flight; _flushRun is
    // its promise, which a caller arriving mid-drain awaits (see _flush).
    _flushing: false,
    _flushRun: null as Promise<boolean> | null,
    // See CmsEditor._placeholderRules. Recorded by _queueEdit for fields that
    // carry data-cms-placeholders; _flushOnce holds back any such field whose
    // value fails the server's placeholder grammar (no PUT, no toast — the
    // field shows an inline error) and it stays queued, so the editor stays
    // dirty and publish is blocked until the operator fixes it.
    _placeholderRules: {} as Record<string, string[]>,
    // The rail section each placeholder-ruled field was edited in (same key
    // as _placeholderRules) — where Publish navigates when the held-back
    // field is off-screen.
    _placeholderSections: {} as Record<string, string>,
    // Set by _focusFirstInvalidPlaceholder when it had to navigate; the
    // post-swap _restoreInvalidPlaceholderFields consumes it and focuses.
    _placeholderFocusPending: false,
    // The field's last SERVER value per ruled field (same key): its rendered
    // value when first edited, then each value that saved. A re-render whose
    // value differs from it means someone else (assistant, revert) wrote the
    // field meanwhile — that write wins over a held-back invalid edit.
    _placeholderBaselines: {} as Record<string, string>,

    _onFieldEvent(e: Event) {
      const el = e.target as HTMLElement | null;
      const field = el?.dataset?.cmsField;
      if (!field) return;
      // A suppressed machine fill (see _setFieldValue / onCmsFieldFilled) is
      // NOT a human edit — consume the one-shot flag and skip both the
      // autosave re-arm below AND the AI-layer clear (port of
      // wizard-autosave.ts's data-autosave-suppress guard).
      if (el.dataset.cmsAutosaveSuppress) {
        delete el.dataset.cmsAutosaveSuppress;
        return;
      }
      // Genuine operator edit: if this field still carries an unresolved AI
      // layer, the operator has now reviewed/overridden it — clear the
      // marker + badge (port of wizard-autosave.ts's clearAiMark; design spec
      // §7.2: "preAI is cleared the moment the user manually edits the field
      // after an AI edit") so a later revert-button click pops straight to
      // published.
      this._clearAiLayerOnEdit(field);
      // Placeholder-bearing field: refresh its inline error on every keystroke
      // and pass its allowed list so the flush can hold an invalid value back.
      let placeholders: string[] | undefined;
      if (el.dataset.cmsPlaceholders !== undefined) {
        placeholders = parseAllowed(el.dataset.cmsPlaceholders);
        const key = placeholderRuleKey(el.dataset.cmsTarget || `property:sections.${this.section}`, field);
        if (!(key in this._placeholderBaselines)) {
          this._placeholderBaselines[key] = (el as HTMLInputElement).defaultValue ?? "";
        }
        renderPlaceholderError(el);
      }
      this._queueEdit(el?.dataset?.cmsTarget, field, (el as HTMLInputElement).value ?? "", placeholders);
    },

    // _onPlaceholderChipClick inserts the chip's {token} into its field (the
    // input inside the same data-cms-placeholder-scope) at the caret; the
    // synthetic input event runs the normal autosave path above.
    _onPlaceholderChipClick(btn: HTMLElement) {
      const name = btn.dataset.cmsPlaceholderInsert;
      const field = btn
        .closest("[data-cms-placeholder-scope]")
        ?.querySelector<HTMLInputElement | HTMLTextAreaElement>("[data-cms-placeholders]");
      if (name && field) insertPlaceholderToken(field, name);
    },

    // _queueEdit records one field edit under its TARGET group and arms the
    // debounced flush. A missing target falls back to the current property
    // section (legacy single-doc fields). The target string is the routing key
    // _flush turns into a (content-path, overlay-shape) pair.
    _queueEdit(target: string | undefined, field: string, value: unknown, placeholders?: string[]) {
      const tgt = target || `property:sections.${this.section}`;
      (this._pending[tgt] ||= {})[field] = value;
      if (placeholders) {
        this._placeholderRules[placeholderRuleKey(tgt, field)] = placeholders;
        this._placeholderSections[placeholderRuleKey(tgt, field)] = this.section;
      }
      if (tgt === "site:design") {
        this._patchPreviewTheme(field, String(value));
        this._updateContrastWarnings();
        // cms-09: re-evaluate which mood the neutrals now match. A manual neutral
        // tweak clears the highlight; a mood-button apply sets it (its four
        // _setNeutralColor dispatches each land here). Cheap no-op off Design.
        this._updateActiveMood();
        // Maps bake colors at boot — CSS-var patching can't recolor them.
        // Re-boot the iframe's maps (debounced) for the colors maps use.
        if (field === "primary_color" || field === "accent_color" || field === "background_color") {
          if (this._recolorTimer) clearTimeout(this._recolorTimer);
          this._recolorTimer = setTimeout(() => {
            const win = this._iframe()?.contentWindow as
              (Window & { __studioPreviewRecolor?: () => void }) | null | undefined;
            win?.__studioPreviewRecolor?.();
          }, 200);
        }
        // These booleans change SERVER-rendered output — the map data-* attrs
        // (buildings_3d/animated_intro/style_follows_theme, read in MapEmbed)
        // and allow_dark_mode's gating — which no CSS-var patch can reflect.
        // site/design's preview mode is the section-wide "theme-live" fast path
        // (editor-preview.ts _refreshPreview bails without re-fetching), so these
        // need an explicit full reload, debounced.
        if (
          field === "style_follows_theme" ||
          field === "buildings_3d" ||
          field === "animated_intro" ||
          field === "allow_dark_mode"
        ) {
          if (this._darkReloadTimer) clearTimeout(this._darkReloadTimer);
          this._darkReloadTimer = setTimeout(() => {
            void this.flushPending().then(() => this._refreshPreviewReload());
          }, 600);
        }
      }
      // Every field (not just Design) now carries the unified revert button
      // (design spec §7.2) — sync it unconditionally.
      this._syncRevertButton(field, String(value));
      this._setDirty(true);
      if (this._saveTimer) clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => void this._flush(), 600);
    },

    // _targetRoute maps an autosave target to its content path + overlay-builder.
    // THIS IS THE LOAD-BEARING SHAPE CONTRACT — it MUST agree with the Go side
    // (service.CMSTarget*, seedEditableKeys, buildCMSEditorData read paths) and
    // with mergeOverlay's deep-merge semantics. The four pinned shapes:
    //
    //   property:sections.<section> → content/properties/{locale}/{slug}.json
    //                                 {sections:{<section>:{<field>:val,…}}}
    //   property:seo                → content/properties/{locale}/{slug}.json
    //                                 {seo:{meta_title,meta_description,og_image_id}}
    //   site:homepage               → content/site/{locale}.json
    //                                 {homepage:{<field>:val,…}}
    //   site:homepage.areas         → content/site/{locale}.json
    //                                 {homepage:{areas:{heading,intro}}}
    //                                 (UNLIKE every other homepage/* section —
    //                                 the areas section's own heading/intro
    //                                 genuinely nest; cycle-1 round-trip fix.
    //                                 The area_cards drag-reorder widget
    //                                 (area-cards.ts) sends field "order"
    //                                 through this SAME target/route, giving
    //                                 {homepage:{areas:{order:[...]}}} — no
    //                                 dedicated branch needed, same reasoning
    //                                 as the area:<slug>:section_order note
    //                                 below about shared routes carrying
    //                                 multiple field shapes.)
    //   site:contact.details        → content/site/{locale}.json
    //                                 {contact:{details:{<field>:val,…}}}
    //   site:brand                  → content/site/{locale}.json
    //                                 {brand:{logo_id,favicon_id,includes_wordmark}, site_name}
    //                                 (site_name is TOP-LEVEL, NOT under brand)
    //   site:nav                    → content/site/{locale}.json
    //                                 {nav:{home,about,contact,properties}}
    //   site:footer                 → content/site/{locale}.json
    //                                 {footer:{tagline}}
    //   site:seo                    → content/site/{locale}.json
    //                                 {seo:{meta_title,meta_description,og_image_id}}
    //   site:<page>[.<child>…]      → content/site/{locale}.json
    //                                 {<page>:{<child>:{<field>:val,…}}}
    //                                 generic route for every registered site-level
    //                                 page target in config.pageTargets
    //                                 (service.CMSPageAutosaveTargets); e.g.
    //                                 site:about → {about:{…}}, site:contact.seo →
    //                                 {contact:{seo:{…}}}
    //   property:card:<slug>        → content/properties/{locale}/{slug}.json
    //                                 {card:{<field>:val,…}}
    //                                 (slug rides in target; config.slug is empty in homepage ctx)
    //   site:property_order         → content/site/{locale}.json
    //                                 {property_order:[<slug>,…]}
    //   property:sections_order     → content/properties/{locale}/{slug}.json
    //                                 {sections_order:[{id,enabled,variant?},…]}
    //   site:homepage.sections      → content/site/{locale}.json
    //                                 {homepage:{sections:[{id,enabled,…},…]}}
    //   site:<page>.sections        → content/site/{locale}.json
    //                                 {<page>:{sections:[{id,enabled,…},…]}}
    //   area:<slug>:section_order   → content/areas/{locale}/<slug>.json
    //                                 {section_order:[<id>,…], hidden_sections:[<id>,…]}
    //                                 (slug rides in target — area, like property:card,
    //                                 is slug-scoped; TWO flat top-level string-array
    //                                 keys, NOT property's single {id,enabled,variant?}
    //                                 array — see service.BuildAreaSectionOrderWrite,
    //                                 which this branch client-side mirrors verbatim)
    //   area:<slug>:item_override:<factId>
    //                                → content/areas/{locale}/<slug>.json
    //                                 {discover:{item_overrides:{<factId>:{<field>:val,…}}}}
    //                                 (T2.8c — the POI board's per-site prose zone,
    //                                 poi-board.ts's updateOverride/_itemOverrideTarget.
    //                                 Slug AND factId both ride in the target — a
    //                                 single shared "area:<slug>:discover" target
    //                                 can't express a per-fact key, so this is a
    //                                 dedicated branch, not a field posted to the
    //                                 generic area:<slug>:<section> branch below.
    //                                 Checked BEFORE that branch — both share the
    //                                 "area:" prefix and this is the more specific
    //                                 match, same precedence reason as
    //                                 :section_order above. T2.4's content-generation
    //                                 merge already applies item_overrides onto the
    //                                 rendered item, so this write closes the
    //                                 round-trip.)
    //   area:<slug>:<section>       → content/areas/{locale}/<slug>.json
    //                                 {<section>:{<field>:val,…}}
    //                                 (slug rides in target, like the section_order
    //                                 branch above — checked AFTER it since both
    //                                 share the "area:" prefix; mirrors
    //                                 property:sections.<section> below. Also used
    //                                 by the POI board's order/hidden whole-value
    //                                 writes — target "area:<slug>:discover" with
    //                                 field "order"/"hidden" — merging into
    //                                 {discover:{order:[…],hidden:[…]}} via this
    //                                 SAME branch, no dedicated route needed.)
    //
    // Returns null for an unrecognized target (the edit is then dropped with a
    // console warning rather than written to the wrong file).
    _targetRoute(
      target: string,
      fields: Record<string, unknown>,
    ): { path: string; overlay: Record<string, unknown> } | null {
      if (target.startsWith("property:sections.")) {
        const section = target.slice("property:sections.".length);
        if (!section) return null;
        return {
          path: `content/properties/${config.locale}/${config.slug}.json`,
          overlay: { sections: { [section]: { ...fields } } },
        };
      }
      // property:seo — per-property PAGE META: the seo block is TOP-LEVEL in the
      // property doc (NOT under sections), mirroring site:seo's top-level shape.
      if (target === "property:seo") {
        return {
          path: `content/properties/${config.locale}/${config.slug}.json`,
          overlay: { seo: { ...fields } },
        };
      }
      const sitePath = `content/site/${config.locale}.json`;
      if (target === "site:homepage") {
        return { path: sitePath, overlay: { homepage: { ...fields } } };
      }
      // site:homepage.areas — the areas section's own heading/intro, UNLIKE
      // every other homepage/* section, nest under homepage.areas (cycle-1
      // round-trip fix; same shape as the generic site:<page>.<child> route below).
      if (target === "site:homepage.areas") {
        return { path: sitePath, overlay: { homepage: { areas: { ...fields } } } };
      }
      if (target === "site:contact.details") {
        return { path: sitePath, overlay: { contact: { details: { ...fields } } } };
      }
      if (target === "site:brand") {
        // site_name is a TOP-LEVEL content/site key (NOT under brand); the other
        // brand fields nest under brand. Split the queued fields accordingly so
        // the overlay matches the pinned shape + the Go seed/read path.
        const brand: Record<string, unknown> = {};
        const top: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(fields)) {
          if (k === "site_name") top[k] = v;
          else brand[k] = v;
        }
        const overlay: Record<string, unknown> = { ...top };
        if (Object.keys(brand).length > 0) overlay.brand = brand;
        return { path: sitePath, overlay };
      }
      if (target === "site:nav") {
        return { path: sitePath, overlay: { nav: { ...fields } } };
      }
      if (target === "site:footer") {
        return { path: sitePath, overlay: { footer: { ...fields } } };
      }
      if (target === "site:seo") {
        return { path: sitePath, overlay: { seo: { ...fields } } };
      }
      // site:<page>[.<child>…] — every registered site-level CMS page
      // (service.CMSPageContexts). The allowlist is server-injected
      // (config.pageTargets = service.CMSPageAutosaveTargets()), so a new page
      // needs no TS change; an unlisted target falls through (→ null, dropped).
      // <page>.sections carries the WHOLE order array ({page:{sections:[…]}}),
      // every other target nests the queued fields along the dotted path
      // (site:contact.seo → {contact:{seo:{…fields}}}).
      if ((config.pageTargets ?? []).includes(target)) {
        const parts = target.slice("site:".length).split(".");
        if (parts.length === 2 && parts[1] === "sections") {
          return { path: sitePath, overlay: { [parts[0]]: { sections: fields.sections } } };
        }
        let overlay: Record<string, unknown> = { ...fields };
        for (let i = parts.length - 1; i >= 0; i--) {
          overlay = { [parts[i]]: overlay };
        }
        return { path: sitePath, overlay };
      }
      // property:card-images:<slug> — the card's image_ids ONLY, written by the
      // embedded image picker (its own Save commits) so images autosave
      // independently of the card modal's transactional Done. Deep-merges into the
      // same card object as property:card:<slug>; the card Done EXCLUDES image_ids.
      // Listed BEFORE property:card: but order is moot — 'property:card-images:'
      // does NOT match startsWith('property:card:') (char 13 is '-', not ':').
      if (target.startsWith("property:card-images:")) {
        const slug = target.slice("property:card-images:".length);
        if (!slug) return null;
        return {
          path: `content/properties/${config.locale}/${slug}.json`,
          overlay: { card: { image_ids: fields.image_ids } },
        };
      }
      // property:card:<slug> — per-property card data in the site locale file.
      // The slug rides in the target because config.slug is empty in the homepage
      // context (the card widget is rendered on the properties overview page).
      if (target.startsWith("property:card:")) {
        const slug = target.slice("property:card:".length);
        if (!slug) return null;
        return {
          path: `content/properties/${config.locale}/${slug}.json`,
          overlay: { card: (fields.card as Record<string, unknown>) ?? {} },
        };
      }
      if (target === "site:property_order") {
        return {
          path: `content/site/${config.locale}.json`,
          overlay: { property_order: fields.property_order },
        };
      }
      if (target === "property:sections_order") {
        return {
          path: `content/properties/${config.locale}/${config.slug}.json`,
          overlay: { sections_order: fields.sections_order },
        };
      }
      // area:<slug>:section_order — the area editor's reorder/toggle rail. The
      // slug rides in the target (area, like property:card, is slug-scoped —
      // config.slug IS populated in the area context, but the target already
      // carries the slug per the CMSTargetAreaSections contract, so parsing it
      // out here — rather than reading config.slug — keeps this branch correct
      // even if a future multi-area-in-one-page surface reuses the same editor
      // shell). Overlay shape is the area contract's TWO top-level flat-string-
      // array keys (section_order + hidden_sections), NOT property's single
      // {id,enabled,variant?} array — this mirrors service.BuildAreaSectionOrderWrite
      // (the Go-side pure translation T1.4 left uncalled) client-side: every
      // section's id goes into section_order in rail order; only the ones the
      // rail marked enabled:false go into hidden_sections.
      if (target.startsWith("area:") && target.endsWith(":section_order")) {
        const slug = target.slice("area:".length, target.length - ":section_order".length);
        if (!slug) return null;
        const sections = (fields.section_order as Array<{ id: string; enabled?: boolean }>) ?? [];
        const order = sections.map((s) => s.id);
        const hidden = sections.filter((s) => s.enabled === false).map((s) => s.id);
        return {
          path: `content/areas/${config.locale}/${slug}.json`,
          overlay: { section_order: order, hidden_sections: hidden },
        };
      }
      // area:<slug>:item_override:<factId> — the POI board's per-site prose
      // zone (T2.8c): title/description overrides for ONE discover fact,
      // keyed by fact id under discover.item_overrides. MUST be checked
      // BEFORE the generic area:<slug>:<section> branch below — both share
      // the "area:" prefix, and this is the more specific match (mirrors
      // :section_order's precedence above). See the shape-contract comment
      // above for why this needs its own branch (a single shared
      // "area:<slug>:discover" target can't express a per-fact key).
      if (target.startsWith("area:") && target.includes(":item_override:")) {
        const rest = target.slice("area:".length); // "<slug>:item_override:<factId>"
        const marker = ":item_override:";
        const idx = rest.indexOf(marker);
        if (idx <= 0) return null;
        const slug = rest.slice(0, idx);
        const factId = rest.slice(idx + marker.length);
        if (!slug || !factId) return null;
        return {
          path: `content/areas/${config.locale}/${slug}.json`,
          overlay: { discover: { item_overrides: { [factId]: { ...fields } } } },
        };
      }
      // area:<slug>:<section> — the area editor's prose/text/markdown fields
      // (hero, intro, discover, seasonal, anreise, cta, seo), stamped by
      // service.CMSTargetAreaSection(slug, section) (handler_cms_editor.go's
      // buildCMSAreaData). MUST be checked AFTER the :section_order AND
      // :item_override branches above — all three share the "area:" prefix,
      // and both of those are more specific matches. Slug rides in the
      // target (like property:card and the area section_order branch), NOT
      // config.slug — see that branch's comment for why. Mirrors
      // property:sections.<section> below, just slug-scoped and rooted at
      // content/areas instead of content/properties. Also the route the POI
      // board's order/hidden whole-value writes use — see the shape-contract
      // comment above.
      if (target.startsWith("area:")) {
        const rest = target.slice("area:".length); // "<slug>:<section>"
        const sep = rest.indexOf(":");
        if (sep <= 0) return null;
        const slug = rest.slice(0, sep);
        const section = rest.slice(sep + 1);
        if (!section) return null;
        return {
          path: `content/areas/${config.locale}/${slug}.json`,
          overlay: { [section]: { ...fields } },
        };
      }
      if (target === "site:homepage.sections") {
        return {
          path: `content/site/${config.locale}.json`,
          overlay: { homepage: { sections: fields.sections } },
        };
      }
      if (target === "site:design") {
        // Flat WebsiteTheme overlay → the DB-derived theme draft path. No locale
        // segment (theme is site-wide). See service.CMSTargetSiteDesign / Publish.
        return { path: "shared/theme.json", overlay: { ...fields } };
      }
      if (target === "site:social_links") {
        // DB-derived overlay → the social-links draft path (NOT content/site).
        // Merged into websites.social_links at publish; never committed to Forgejo.
        return { path: "shared/social-links.json", overlay: { ...fields } };
      }
      return null;
    },

    _onCaptionEvent(e: Event) {
      const d = (e as CustomEvent).detail as {
        imageId: string;
        field: "caption" | "alt";
        value: unknown;
      };
      if (!d?.imageId || !d?.field) return;
      (this._pendingCaptions[d.imageId] ||= {})[d.field] = d.value;
      this._setDirty(true);
      if (this._capTimer) clearTimeout(this._capTimer);
      this._capTimer = setTimeout(() => void this._flushCaptions(), 600);
    },

    // _flushCaptions persists the {images:{<id>:{caption|alt}}} overlay to the
    // same content path as _flush, but as a separate PUT so the two overlays
    // stay shape-distinct ({images:...} vs {sections:...}). On failure, the
    // pending ids are re-queued so the next flushPending() or capTimer retries.
    async _flushCaptions(): Promise<boolean> {
      const imgs = this._pendingCaptions;
      this._pendingCaptions = {};
      const ids = Object.keys(imgs);
      if (ids.length === 0) return true;
      try {
        const resp = await fetch(`${config.baseURL}/api/draft/content/${this._contentPath()}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ images: imgs }),
        });
        if (!resp.ok) throw new Error(`save captions ${resp.status}`);
        // Raw fetch: re-arm the publish surfaces explicitly (plan 2 D11).
        signalPublishStateChanged();
        this._schedulePreview();
        return true;
      } catch (err) {
        console.error("cms caption autosave failed", err);
        for (const id of ids)
          this._pendingCaptions[id] = { ...imgs[id], ...(this._pendingCaptions[id] || {}) };
        dispatchToast(this._msgs.saveFailed, "error");
        return false;
      }
    },

    // flushPending is called from the rail's @htmx:before-request so a pending
    // edit is persisted before the section swaps, and from publish() so both
    // the sections overlay and the captions overlay are flushed before committing.
    // Returns false if either flush fails — publish() gates on this, so a caption
    // save failure correctly aborts the publish with failed captions re-queued.
    async flushPending(): Promise<boolean> {
      if (this._saveTimer) {
        clearTimeout(this._saveTimer);
        this._saveTimer = null;
      }
      if (this._capTimer) {
        clearTimeout(this._capTimer);
        this._capTimer = null;
      }
      const a = await this._flush();
      const b = await this._flushCaptions();
      return a && b;
    },

    // _flush drains _pending — grouped by autosave TARGET — and issues ONE PUT
    // per (content-path, overlay-shape) group. A single edit session can touch
    // up to ~3 targets across 2 files (property section + site homepage + site
    // contact.details); the draft table is per-path so that's expected. Returns
    // true on full success (or nothing-to-do), false if ANY group failed (the
    // failed group's edits are re-queued so the next flush/publish retries).
    // The overlay shapes are pinned in _targetRoute.
    async _flush(): Promise<boolean> {
      // Single-flight. A caller that arrives while a drain is in flight must NOT
      // report success early: a layout switch or the publish gate would then
      // write/ship while the save is still pending, and the form it re-renders
      // could show pre-edit content. It waits for the in-flight drain instead.
      // If that drain failed, its edits are already re-queued and the failure is
      // already toasted, so the waiter stops here (no second PUT) and returns
      // false. If it succeeded, the waiter drains whatever was queued meanwhile.
      if (this._flushing && this._flushRun) {
        if (!(await this._flushRun)) return false;
        return this._flush();
      }
      this._flushing = true;
      this._flushRun = this._flushOnce();
      try {
        return await this._flushRun;
      } finally {
        this._flushing = false;
        this._flushRun = null;
      }
    },

    // _flushOnce is the actual drain body — never call it directly; go through
    // _flush so the single-flight lock stays in force.
    async _flushOnce(): Promise<boolean> {
      const pending = this._pending;
      this._pending = {};
      const targets = Object.keys(pending);
      if (targets.length === 0) return true;

      let allOk = true;
      // unexplained: some failed target did NOT already toast the server's
      // specific reason (a 400 content-rule rejection) — only then does the
      // generic saveFailed toast fire.
      let unexplained = false;
      let anyWrote = false;
      for (const target of targets) {
        let fields = pending[target];
        if (!fields || Object.keys(fields).length === 0) continue;
        // Hold back placeholder-invalid fields (half-typed "{gue", unknown
        // names): they would be rejected server-side and take the whole
        // batch down with them. They stay queued (dirty, publish-gated) and
        // save on the edit that makes them valid. The rest of the batch saves.
        const held = this._invalidPlaceholderFields(target, fields);
        if (held.length > 0) {
          const requeue = (this._pending[target] ||= {});
          const rest: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(fields)) {
            if (held.includes(k)) {
              if (!(k in requeue)) requeue[k] = v;
            } else {
              rest[k] = v;
            }
          }
          allOk = false;
          fields = rest;
          if (Object.keys(fields).length === 0) continue;
        }
        const route = this._targetRoute(target, fields);
        if (!route) {
          console.error("cms autosave: unrecognized target, dropping edit", target);
          // Do NOT silently discard: mark the flush failed so publish()'s gate
          // (flushPending) aborts instead of shipping a draft missing this edit,
          // and emit the per-target failure ack so any gate armed on this target
          // (e.g. the image picker's _saving) releases. The saveFailed toast
          // fires once below (unexplained failure). Symmetric to the catch path.
          allOk = false;
          unexplained = true;
          document.body.dispatchEvent(
            new CustomEvent("cms-autosave-failed", { detail: { target }, bubbles: true }),
          );
          continue;
        }
        let explained = false;
        try {
          const resp = await fetch(`${config.baseURL}/api/draft/content/${route.path}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
            body: JSON.stringify(route.overlay),
          });
          if (!resp.ok) {
            // A 400 carries a translated, specific reason (e.g. unknown
            // placeholder) — show it instead of the generic "save failed".
            // The edit stays queued, so publish stays gated until it's fixed.
            if (resp.status === 400 && (await toastFetchError(resp))) explained = true;
            throw new Error(`save ${resp.status}`);
          }
          anyWrote = true;
          for (const [k, v] of Object.entries(fields)) {
            const key = placeholderRuleKey(target, k);
            if (key in this._placeholderRules && typeof v === "string") this._placeholderBaselines[key] = v;
          }
          // Raw fetch: re-arm the publish surfaces explicitly (plan 2 D11).
          signalPublishStateChanged();
          // Ack a completed per-TARGET autosave so listeners gated on persisted
          // state can proceed (e.g. the image picker's remove-from-pool, which
          // must read committed content). Emitted ONLY on PUT success — a failed
          // target re-queues below and must NOT ack.
          document.body.dispatchEvent(
            new CustomEvent("cms-autosave-flushed", { detail: { target }, bubbles: true }),
          );
        } catch (err) {
          console.error("cms autosave failed", target, err);
          // Re-queue this target's edits (don't clobber edits queued since).
          const requeue = (this._pending[target] ||= {});
          for (const k of Object.keys(fields)) if (!(k in requeue)) requeue[k] = fields[k];
          allOk = false;
          if (!explained) unexplained = true;
          // Signal terminal failure for THIS target so listeners that armed a
          // saving-gate on dispatch can release it — the success ack
          // (cms-autosave-flushed) never fires for a failed PUT, so without
          // this the gate would stick until reload. Symmetric to the ack above.
          document.body.dispatchEvent(
            new CustomEvent("cms-autosave-failed", { detail: { target }, bubbles: true }),
          );
        }
      }
      if (anyWrote) this._schedulePreview();
      if (!allOk && unexplained) dispatchToast(this._msgs.saveFailed, "error");
      return allOk;
    },

    // _hasPending reports whether any TARGET group still holds field edits — the
    // publish gate (a failed flush re-queues edits under their target).
    _hasPending(): boolean {
      return Object.values(this._pending).some((g) => g && Object.keys(g).length > 0);
    },

    // _invalidPlaceholderFields lists the queued fields of one target whose
    // value fails their recorded placeholder rule.
    _invalidPlaceholderFields(target: string, fields: Record<string, unknown>): string[] {
      const out: string[] = [];
      for (const [k, v] of Object.entries(fields)) {
        const allowed = this._placeholderRules[placeholderRuleKey(target, k)];
        if (allowed && typeof v === "string" && checkContentPlaceholders(v, allowed)) out.push(k);
      }
      return out;
    },

    // _hasInvalidPlaceholders: some queued edit is held back by its
    // placeholder rule — publish reports this instead of a generic failure.
    _hasInvalidPlaceholders(): boolean {
      return Object.entries(this._pending).some(
        ([t, g]) => !!g && this._invalidPlaceholderFields(t, g).length > 0,
      );
    },

    // _invalidPlaceholderElements yields the on-screen inputs of held-back
    // fields, paired with their queued value.
    _invalidPlaceholderElements(): Array<[HTMLInputElement, string, string, string]> {
      // document, not this.$el: called from an @click expression, Alpine's
      // $el is the clicked element (the Publish button), not the editor root.
      const root = typeof document !== "undefined" ? document : undefined;
      if (!root?.querySelector) return [];
      const out: Array<[HTMLInputElement, string, string, string]> = [];
      for (const [t, g] of Object.entries(this._pending)) {
        if (!g) continue;
        for (const k of this._invalidPlaceholderFields(t, g)) {
          const el = root.querySelector<HTMLInputElement>(
            `[data-cms-target="${this._cssEscape(t)}"][data-cms-field="${this._cssEscape(k)}"][data-cms-placeholders]`,
          );
          if (el) out.push([el, String(g[k]), t, k]);
        }
      }
      return out;
    },

    // _focusFirstInvalidPlaceholder takes the operator to the first held-back
    // field: focus it when it's on screen, else navigate to its rail section
    // (the normal rail link click — flush, leftpane swap, history) and focus
    // it once the swap has re-rendered it (_restoreInvalidPlaceholderFields).
    // _blockOnInvalidPlaceholders is the single guard for every action that
    // would drop the held-back edit (publish, body-swap navigation, history
    // traversal, staging + refresh): when an invalid placeholder edit is
    // held, it says why (toast) and takes the operator to the field, and
    // returns true — the caller must then NOT proceed.
    _blockOnInvalidPlaceholders(): boolean {
      if (!this._hasInvalidPlaceholders()) return false;
      dispatchToast(this._msgs.placeholderBlocked || this._msgs.saveFailed, "error");
      this._focusFirstInvalidPlaceholder();
      return true;
    },

    _focusFirstInvalidPlaceholder() {
      const first = this._invalidPlaceholderElements()[0];
      if (first) {
        first[0].focus();
        return;
      }
      const root = typeof document !== "undefined" ? document : undefined;
      for (const [t, g] of Object.entries(this._pending)) {
        if (!g) continue;
        for (const k of this._invalidPlaceholderFields(t, g)) {
          const section = this._placeholderSections[placeholderRuleKey(t, k)];
          const link = section
            ? root?.querySelector<HTMLElement>(`a[data-cms-rail-link="${this._cssEscape(section)}"]`)
            : null;
          if (link) {
            this._placeholderFocusPending = true;
            link.click();
            return;
          }
        }
      }
    },

    // _restoreInvalidPlaceholderFields runs after a left-pane re-render: a
    // held-back (unsaved) value would otherwise be replaced by the last saved
    // one while still queued — put it back and re-show its error.
    // An external write since the hold (re-rendered value != the field's last
    // server value) wins: the held value is dropped instead. A restore fires
    // a (suppressed) input so the field's own listeners — counter, auto-
    // resize — catch up, without re-queuing or clearing an AI layer.
    _restoreInvalidPlaceholderFields() {
      const restored: HTMLInputElement[] = [];
      for (const [el, v, t, k] of this._invalidPlaceholderElements()) {
        const key = placeholderRuleKey(t, k);
        const baseline = this._placeholderBaselines[key];
        if (baseline !== undefined && el.defaultValue !== baseline) {
          const g = this._pending[t];
          if (g) {
            delete g[k];
            if (Object.keys(g).length === 0) delete this._pending[t];
          }
          delete this._placeholderBaselines[key];
          renderPlaceholderError(el);
          continue;
        }
        if (el.value !== v) {
          el.value = v;
          el.dataset.cmsAutosaveSuppress = "1";
          el.dispatchEvent(new Event("input", { bubbles: true }));
          delete el.dataset.cmsAutosaveSuppress;
        }
        renderPlaceholderError(el);
        restored.push(el);
      }
      if (this._placeholderFocusPending && restored.length > 0) {
        this._placeholderFocusPending = false;
        restored[0].focus();
      }
    },
  };
}
